import React, { useState, useEffect, useCallback } from "react";

import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import {
  fetchZohoStatus,
  fetchZohoSyncStatus,
  syncZohoCustomers,
  syncZohoMaintenanceReportsInbound,
  pushZohoMaintenanceReportsOutbound,
  pushZohoCustomerPlotsOutbound,
  syncZohoIssuesInbound,
  pushZohoIssuesOutbound,
  syncZohoPlotsMaster,
  syncZohoDuePlots,
  fetchZohoUpdateStatus,
} from "../api/api";

const SYNC_DEFINITIONS = [
  {
    key: "customers",
    title: "Customer & Plot Sync",
    direction: "Zoho → App",
    description: "Pulls Customer_Entry_Report from Zoho Creator into our Users/Plots/Zones.",
    run: () => syncZohoCustomers(false),
    summarize: (data) =>
      `${data.plots_created} plot(s) created, ${data.plots_updated} updated, ${data.users_created} user(s) created` +
      (data.errors?.length ? ` — ${data.errors.length} error(s)` : ""),
  },
  {
    key: "maintenance_reports_inbound",
    title: "Maintenance Reports Sync",
    direction: "Zoho → App",
    description: "Pulls reports submitted directly in the Zoho Creator app into our Maintenance Reports.",
    run: () => syncZohoMaintenanceReportsInbound(false),
    summarize: (data) =>
      `${data.reports_created} report(s) created, ${data.reports_updated} updated` +
      (data.errors?.length ? ` — ${data.errors.length} error(s)` : ""),
  },
  {
    key: "maintenance_reports_outbound",
    title: "Maintenance Reports Push",
    direction: "App → Zoho",
    description: "Retries pushing any locally-submitted reports that failed to reach Zoho at submit time.",
    run: () => pushZohoMaintenanceReportsOutbound(),
    summarize: (data) =>
      data.total_attempted === 0
        ? "Nothing to push — everything already synced"
        : `${data.pushed} of ${data.total_attempted} pushed` + (data.still_failed ? `, ${data.still_failed} still failing` : ""),
  },
  {
    key: "customer_plots_outbound",
    title: "Customer & Plot Push",
    direction: "App → Zoho",
    description: "Pushes customers created + assigned a plot (or existing customers assigned a plot) that haven't reached Zoho yet.",
    run: () => pushZohoCustomerPlotsOutbound(),
    summarize: (data) =>
      data.total_attempted === 0
        ? "Nothing to push — everything already synced"
        : `${data.pushed} of ${data.total_attempted} pushed` + (data.still_failed ? `, ${data.still_failed} still failing` : ""),
  },
  {
    key: "issues_inbound",
    title: "Issues Sync",
    direction: "Zoho → App",
    description: "Pulls complaints submitted directly in the Zoho Creator app (Customer_Complaint) into our Issues.",
    run: () => syncZohoIssuesInbound(false),
    summarize: (data) =>
      `${data.requests_created} request(s) created, ${data.requests_updated} updated` +
      (data.errors?.length ? ` — ${data.errors.length} error(s)` : ""),
  },
  {
    key: "issues_outbound",
    title: "Issues Push",
    direction: "App → Zoho",
    description: "Retries pushing any locally-raised/updated issues that failed to reach Zoho.",
    run: () => pushZohoIssuesOutbound(),
    summarize: (data) =>
      data.total_attempted === 0
        ? "Nothing to push — everything already synced"
        : `${data.pushed} of ${data.total_attempted} pushed` + (data.still_failed ? `, ${data.still_failed} still failing` : ""),
  },
  {
    key: "plots_master_inbound",
    title: "Plots Master Sync",
    direction: "Zoho → App",
    description: "Pulls Plot_dB_Master_Report (Supervisor, Maintenance Status, dates, amenities) into plots — creates a plot straight from this report when Customer_Entry_Report never had one for it. Only rows Zoho shows as changed since the last sync are touched.",
    run: () => syncZohoPlotsMaster(false),
    summarize: (data) =>
      `${data.plots_created} created, ${data.plots_updated} updated, ${data.skipped_unchanged} unchanged` +
      (data.errors?.length ? ` — ${data.errors.length} error(s)` : ""),
    // One-time repair option: re-touches every row regardless of whether
    // Zoho shows it as changed. Only needed to fix rows an earlier bug in
    // this sync already marked "up to date" despite something (e.g.
    // Supervisor) never actually resolving — a normal Sync Now can't reach
    // those on its own since Zoho's own Modified_Time hasn't moved.
    forceRun: () => syncZohoPlotsMaster(false, true),
    forceLabel: "Force Full Resync (repair)",
  },
  {
    key: "due_plots_inbound",
    title: "Due Plots Sync",
    direction: "Zoho → App",
    description: "Pulls Due_Plots, Overdue_Plots, Water_Collection_Due and Plot_Collection_Due (maintenance/collections-due reports covering every supervisor) into a read-only mirror. See Due Plots to view the results grouped by supervisor.",
    run: () => syncZohoDuePlots(false),
    summarize: (data) => {
      const parts = ["due", "overdue", "water_collection_due", "plot_collection_due"];
      const labels = { due: "due", overdue: "overdue", water_collection_due: "water collection due", plot_collection_due: "plot collection due" };
      const totalErrors = parts.reduce((sum, k) => sum + (data[k]?.errors?.length || 0), 0);
      return (
        parts.map((k) => `${data[k].rows_stored} ${labels[k]}`).join(", ") + " plot(s) synced" +
        (totalErrors ? ` — ${totalErrors} error(s)` : "")
      );
    },
  },
];

function formatElapsed(totalSeconds) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
}

function StatusBadge({ success }) {
  if (success === null || success === undefined) {
    return <span style={{ color: "#666" }}>— Never run</span>;
  }
  return success ? (
    <span style={{ color: "#2e7d32", fontWeight: 600 }}>✅ Success</span>
  ) : (
    <span style={{ color: "#dc2626", fontWeight: 600 }}>❌ Failed</span>
  );
}

function ZohoIntegration() {

  const [zohoStatus, setZohoStatus] = useState(null);
  const [syncStatus, setSyncStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [runningKey, setRunningKey] = useState(null);
  const [runResults, setRunResults] = useState({}); // key -> { message, isError }
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  // Defaults to false (hide App → Zoho cards) until the backend confirms
  // otherwise — see Settings.ZOHO_UPDATE. Outbound pushes write app data
  // back into Zoho, which is exactly what's off when this flag is off.
  const [zohoUpdateEnabled, setZohoUpdateEnabled] = useState(false);

  // These syncs are a single blocking request/response — the backend has no
  // way to report partial progress mid-run, so there's nothing real to show
  // a progress bar for. A ticking elapsed-time counter is the honest
  // substitute: it at least proves the request is still alive rather than
  // hung, since some of these (e.g. Customer & Plot Sync, ~800+ Zoho rows)
  // can legitimately take a couple of minutes.
  useEffect(() => {
    if (!runningKey) {
      setElapsedSeconds(0);
      return;
    }
    setElapsedSeconds(0);
    const start = Date.now();
    const interval = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [runningKey]);

  const loadStatus = useCallback(async () => {
    setError("");
    try {
      const [status, sync] = await Promise.all([fetchZohoStatus(), fetchZohoSyncStatus()]);
      setZohoStatus(status);
      setSyncStatus(sync);
    } catch (err) {
      setError("Could not load Zoho integration status.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
    fetchZohoUpdateStatus().then((r) => setZohoUpdateEnabled(r.enabled)).catch(() => {});
  }, [loadStatus]);

  const visibleDefinitions = SYNC_DEFINITIONS.filter(
    (def) => zohoUpdateEnabled || def.direction !== "App → Zoho"
  );

  const handleRun = async (def, runFn = def.run) => {
    setRunningKey(def.key);
    setRunResults((prev) => ({ ...prev, [def.key]: null }));
    try {
      const data = await runFn();
      setRunResults((prev) => ({ ...prev, [def.key]: { message: def.summarize(data), isError: false } }));
    } catch (err) {
      const detail = err.response?.data?.detail || "Sync failed. Please try again.";
      setRunResults((prev) => ({ ...prev, [def.key]: { message: detail, isError: true } }));
    } finally {
      setRunningKey(null);
      await loadStatus();
    }
  };

  return (
    <div>
      <Navbar />
      <Sidebar />
      <div className="main-content">

        <h1>Zoho Integration</h1>

        {error && <p className="login-error" style={{ color: "red" }}>{error}</p>}
        {loading && <p>Loading…</p>}
        {!zohoUpdateEnabled && (
          <p style={{ color: "#666" }}>
            Outbound syncs (App → Zoho) are hidden — Zoho is the source of truth (ZOHO_UPDATE is off).
          </p>
        )}

        {zohoStatus && (
          <div className="section-card">
            <p>
              <strong>Zoho configured:</strong> {zohoStatus.configured ? "✅ Yes" : "❌ No"}
              {"  ·  "}
              <strong>Connected:</strong> {zohoStatus.connected ? "✅ Yes" : "❌ No"}
            </p>
            {!zohoStatus.connected && (
              <p style={{ color: "#dc2626" }}>
                Zoho isn't connected yet — set ZOHO_REFRESH_TOKEN or connect via the self-client grant token flow before running a sync.
              </p>
            )}
          </div>
        )}

        <div className="dashboard-grid">
          {visibleDefinitions.map((def) => {
            const status = syncStatus[def.key] || {};
            const result = runResults[def.key];
            const isRunning = runningKey === def.key;

            return (
              <div className="card" key={def.key}>
                <h3>{def.title}</h3>
                <p style={{ color: "#666", fontSize: "13px" }}>{def.direction}</p>
                <p>{def.description}</p>

                <p>
                  <strong>Last synced:</strong>{" "}
                  {status.last_synced_at ? new Date(status.last_synced_at).toLocaleString() : "Never"}
                </p>
                <p>
                  <strong>Status:</strong> <StatusBadge success={status.success} />
                </p>
                {status.summary && <p style={{ color: "#666", fontSize: "13px" }}>{status.summary}</p>}

                <button
                  className="login-btn"
                  style={{ width: "auto", padding: "8px 16px" }}
                  onClick={() => handleRun(def)}
                  disabled={isRunning || !zohoStatus?.connected}
                >
                  {isRunning ? `Syncing… (${formatElapsed(elapsedSeconds)})` : "Sync Now"}
                </button>

                {def.forceRun && (
                  <button
                    className="login-btn"
                    style={{ width: "auto", padding: "8px 16px", marginLeft: "10px", background: "#666" }}
                    onClick={() => {
                      if (window.confirm(
                        "This re-touches every row from Zoho, ignoring what's already marked up to date. " +
                        "Only needed to repair rows a past bug left stuck — routine syncs should use " +
                        "\"Sync Now\" instead. Continue?"
                      )) {
                        handleRun(def, def.forceRun);
                      }
                    }}
                    disabled={isRunning || !zohoStatus?.connected}
                  >
                    {def.forceLabel}
                  </button>
                )}

                {isRunning && (
                  <p style={{ color: "#666", fontSize: "13px", marginTop: "10px" }}>
                    Still running — larger reports (customers/plots, service requests) can take a couple of
                    minutes. Please don't navigate away or run it again.
                  </p>
                )}

                {result && (
                  <p style={{ color: result.isError ? "#dc2626" : "#2e7d32", marginTop: "10px" }}>
                    {result.message}
                  </p>
                )}
              </div>
            );
          })}
        </div>

      </div>
    </div>
  );
}

export default ZohoIntegration;
